import React from 'react';
import { CheckIcon, StarIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
type Volume = {
  number: string;
  title: string;
  subtitle: string;
  body: string;
  promise: string;
  flag?: string;
};
const volumes: Volume[] = [{
  number: 'Volume 1',
  title: 'The Discovery & Onboarding Manifesto',
  subtitle: 'From Lead to Contract: Mastering the Client Entrance Experience',
  body: 'Turn inquiries into signed, high-paying clients. Word-for-word Sales Scripts, The Discovery Call Framework, the Welcome Magazine structure that makes you look instantly premium, and The Client Boundary Agreement that protects your time from day one.',
  promise: 'No more getting ghosted after you send the quote.'
}, {
  number: 'Volume 2',
  title: "The Architect's Blueprint",
  subtitle: 'Advanced Logistics, Budgeting & Production Planning',
  body: 'The “hard side” of the business, handled. Manage five- and six-figure budgets without costly mistakes with The Pro-Planner Budget Master, map the full Guest Experience, master Technical Floor Plans, and run everything through the Logistics Master Checklist.',
  promise: 'Numbers stop being scary. You stop losing money.'
}, {
  number: 'Volume 3',
  title: 'The Design & Aesthetic Strategy',
  subtitle: "Translating Dreams into Reality: The Creative Director's Handbook",
  body: 'Turn a vague “romantic but modern, you know?” into a cohesive, high-end visual experience. Moodboarding Like a Pro, Color Theory for Weddings, Floral Design Briefings, and Lighting & Decor Production.',
  promise: 'This is what makes your weddings look expensive — and get featured.'
}, {
  number: 'Volume 4',
  title: 'The Vendor Command & Control',
  subtitle: 'Building Your A-Team: Vendor Management & Coordination Excellence',
  body: 'Never get walked over again. The Vendor Selection Matrix, Contract Review Checklists, Production Meeting Agendas, and The Vendor Communication Flow that positions you as the creative director every vendor answers to.',
  promise: 'You lead. They follow.'
}, {
  number: 'Volume 5',
  title: 'The “Day-Of” Emergency Protocols',
  subtitle: 'Execution Under Pressure: The Ultimate Wedding Day Operations Manual',
  body: 'Zero improvisation for the 24 hours that matter most. The Master Production Timeline (minute by minute), Load-in/Load-out Schedules, The Emergency Kit Inventory, and Conflict Resolution Scripts for every “oh no” moment.',
  promise: 'You stay calm while everyone else panics. That’s exactly what couples pay premium for.',
  flag: 'The heart of the system'
}, {
  number: 'Volume 6',
  title: 'The Profit & Legacy System',
  subtitle: 'Beyond the Sparklers: Offboarding, Referrals & Business Scalability',
  body: 'What happens after the couple leaves for the honeymoon decides whether you have a hobby or a business. The Offboarding Workflow, the Review Generation Machine, The Referral Program for Vendors, and your Annual Business Review & Scaling plan.',
  promise: 'Turn every wedding into your next three clients.'
}];
const includes = ['Step-by-step frameworks', 'Word-for-word scripts', 'Printable checklists & templates', 'Client-facing materials', 'Universal systems — any country, any venue, any budget'];
export function Tomes() {
  return <section className="w-full bg-ivory py-16 sm:py-24" data-id="element-233" data-mp="1ajx7dndzur0v">
      <div className="mx-auto max-w-6xl px-5" data-id="element-234" data-mp="dxaj0r2qa9od">
        <h2 className="text-center font-serif text-3xl sm:text-5xl text-ink" data-id="element-235" data-mp="iv3mhccncpky">
          Six volumes. One complete business. Zero improvisation.
        </h2>
        <div className="rule-gold mx-auto mt-8 w-32" aria-hidden="true" data-id="element-236" data-mp="mv4c7t574n7b" />

        <ul className="mt-14 grid gap-6 lg:grid-cols-3" data-id="element-237" data-mp="rofjtwhu180a">
          {volumes.map(volume => <li key={volume.number} className={`flex flex-col border bg-white p-7 transition-shadow duration-300 hover:shadow-card ${volume.flag ? 'border-gold' : 'border-gold-soft/70'}`} data-id="element-238" data-mp="3rbouoqxgfen0">
              <div className="flex items-center justify-between gap-3" data-id="element-239" data-mp="378sse7vw7cg1">
                <span className="text-[10px] uppercase tracking-[0.24em] text-gold-dark" data-id="element-240" data-mp="37alynm2lfuni">
                  {volume.number}
                </span>
                {volume.flag && <span className="inline-flex items-center gap-1.5 bg-gold-tint px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] text-gold-dark" data-id="element-241" data-mp="3h5dxet7wwnjv">
                    <StarIcon className="h-3 w-3 fill-gold text-gold" aria-hidden="true" data-id="element-242" data-mp="3pzitps5ter6s" />
                    {volume.flag}
                  </span>}
              </div>

              <h3 className="mt-4 font-serif text-2xl leading-snug text-ink" data-id="element-243" data-mp="3s2gxdhjk76y">
                {volume.title}
              </h3>
              <p className="mt-2 text-xs uppercase tracking-[0.1em] text-ink-muted" data-id="element-244" data-mp="2ai0bx951ridq">
                {volume.subtitle}
              </p>
              <p className="mt-5 flex-1 text-[15px] leading-relaxed text-ink-soft" data-id="element-245" data-mp="25l03wson05jz">
                {volume.body}
              </p>
              <p className="mt-6 border-t border-gold-soft/70 pt-5 font-serif text-base italic text-gold-dark" data-id="element-246" data-mp="q0hnkp5brm19">
                → {volume.promise}
              </p>
            </li>)}
        </ul>

        <div className="mt-12 border border-gold-soft bg-gold-tint px-6 py-8" data-id="element-247" data-mp="gaob7lxcz2vy">
          <p className="text-center text-[11px] uppercase tracking-[0.24em] text-ink-soft" data-id="element-248" data-mp="g8lfcl9iyfjb">
            Inside every volume
          </p>
          <ul className="mt-5 flex flex-wrap items-center justify-center gap-x-7 gap-y-3" data-id="element-249" data-mp="6d06c361gf02">
            {includes.map(item => <li key={item} className="inline-flex items-center gap-2 text-sm text-ink" data-id="element-250" data-mp="3ojo8bk5skm1i">
                <CheckIcon className="h-4 w-4 text-gold-dark" aria-hidden="true" data-id="element-251" data-mp="3thzhdso9isio" />
                {item}
              </li>)}
          </ul>
        </div>

        <div className="mt-12 flex justify-center" data-id="element-252" data-mp="1rpl6hdk4yobr">
          <CtaButton data-id="element-253" data-mp="1rp1vyhwtf27r">I want the complete system</CtaButton>
        </div>
      </div>
    </section>;
}